/**
 * Centralized Subscription Configuration
 * 
 * CRITICAL: This file is manually synchronized using SUBSCRIPTION_SYNC.md.
 * If you modify this file, you must also update:
 * - iOS: ar-catalog/ar-catalog/Domain/Models/SubscriptionConfig.swift
 * - Backend: functions/src/utils/subscriptionConfig.ts
 * - Rules: firestore.rules
 */

export type SubscriptionTierType = 'free' | 'essential' | 'premium' | 'platinum';

export interface TierLimit {
    catalogs: number | 'Unlimited';
    objects: number;
    bandwidthGB: number; // Approximate, for display
    maxViews: number;
}

export interface PlanDetail {
    tier: SubscriptionTierType;
    name: string;
    monthlyPrice: string;
    yearlyPrice: string;
    catalogs: string;
    objects: string;
    bandwidth: string;
    features: string[];
    highlight?: string;
}

export const SUBSCRIPTION_TIERS: Record<SubscriptionTierType, TierLimit> = {
    free: {
        catalogs: 1,
        objects: 10,
        bandwidthGB: 0.5, // 500MB
        maxViews: 25,
    },
    essential: {
        catalogs: 3,
        objects: 20,
        bandwidthGB: 5,
        maxViews: 250,
    },
    premium: {
        catalogs: 10,
        objects: 100,
        bandwidthGB: 50,
        maxViews: 2500,
    },
    platinum: {
        catalogs: 'Unlimited',
        objects: 500,
        bandwidthGB: 200,
        maxViews: 10000,
    },
};

export const SUBSCRIPTION_PLANS: PlanDetail[] = [
    {
        tier: 'free',
        name: 'Free',
        monthlyPrice: 'Free',
        yearlyPrice: 'Free',
        catalogs: '1 Catalog',
        objects: '10 Items',
        bandwidth: '500MB Bandwidth (~25 views)',
        features: [
            '1 Catalog',
            '10 Items',
            'iOS Scanning',
            "QR Code Catalog/AR Item Sharing",
            "Catalog web view",
            'No Analytics'
        ],
        highlight: 'Try it out',
    },
    {
        tier: 'essential',
        name: 'Essential',
        monthlyPrice: '$19',
        yearlyPrice: '$199', // Placeholder
        catalogs: '3 Catalogs',
        objects: '20 Items',
        bandwidth: '5GB Bandwidth (~250 views)',
        features: [
            '3 Catalogs',
            '20 Items',
            'iOS Scanning',
            'Catalog Management via Web',
            "QR Code Catalog/AR Item Sharing",
            "Catalog web view",
            'Analytics Enabled',
            'Email Support'
        ],
    },
    {
        tier: 'premium',
        name: 'Premium',
        monthlyPrice: '$49',
        yearlyPrice: '$499',
        catalogs: '10 Catalogs',
        objects: '100 Items',
        bandwidth: '50GB Bandwidth (~2,500 views)',
        features: [
            '10 Catalogs',
            '100 Items',
            'iOS Scanning',
            'Catalog Management via Web',
            "QR Code Catalog/AR Item Sharing",
            "Catalog web view",
            'Analytics Enabled',
            'Priority Support'
        ],
        highlight: 'Most Popular',
    },
    {
        tier: 'platinum',
        name: 'Platinum',
        monthlyPrice: '$149',
        yearlyPrice: '$1,499',
        catalogs: 'Unlimited Catalogs',
        objects: '500 Items',
        bandwidth: '200GB Bandwidth (~10,000 views)',
        features: [
            'Unlimited Catalogs',
            '500 Items',
            'iOS Scanning',
            'Catalog Management via Web',
            "QR Code Catalog/AR Item Sharing",
            "Catalog web view",
            'Analytics Enabled',
            'Priority Support',
            'High Volume / Enterprise'
        ],
    },
];

// Resolves subscription tier string to a valid type
export const resolveTier = (tierStr?: string): SubscriptionTierType => {
    if (!tierStr) return 'free';
    // Legacy 'basic' is strictly removed per requirements
    // if (tierStr === 'basic') return 'essential';

    // Validate if it is a known tier
    const validTiers: SubscriptionTierType[] = ['free', 'essential', 'premium', 'platinum'];
    if (validTiers.includes(tierStr as SubscriptionTierType)) {
        return tierStr as SubscriptionTierType;
    }

    return 'free'; // Fallback for unknown tiers
};
